/**
 * apps/customer/src/lib/probeCommand.ts
 *
 * Builds the exact one-liner the Customs page hands a customer to run the
 * hardware inventory probe (SIL-695/710/732). Extracted from customs.tsx so
 * the string-building is unit-testable without rendering the route.
 *
 * The command is TOKEN-GATED, not print-mode: earlier versions of this page
 * published a static `--print` command that made zero network calls, which
 * meant the copied command never actually sent anything to SiliconX. That is
 * gone. There is no command to copy until an inventory token has been minted
 * — `buildProbeCommand` returns null in that case, and the page must not
 * render a runnable or partially-runnable command when it does. Once a token
 * exists, it is baked into the one-liner itself (scoped to that single
 * command via an env-var prefix / `$env:` assignment) so the whole thing can
 * be copied and pasted verbatim — no editing the command, no editing the
 * downloaded script.
 *
 * The plaintext token lives only in the page's client state (shown once, at
 * mint) and is never persisted, so a reload always lands back in the locked
 * (no-token) state — that is intended, not a bug: the user mints a fresh
 * token rather than one this session can no longer show them.
 */
import {
  INVENTORY_PROBE_FILENAME,
  INVENTORY_PROBE_SHA256,
  INVENTORY_PROBE_PS1_FILENAME,
  INVENTORY_PROBE_PS1_SHA256,
} from '@siliconx/shared/inventory/probe-pins';

const RUNNER_BASE = 'https://verify.siliconx.tech/runner';
export const PROBE_URL = `${RUNNER_BASE}/${INVENTORY_PROBE_FILENAME}`;
export const PROBE_PS1_URL = `${RUNNER_BASE}/${INVENTORY_PROBE_PS1_FILENAME}`;

export type ProbeOs = 'linux' | 'windows';

export const PROBE_OS_LABELS: Record<ProbeOs, string> = {
  linux: 'Linux',
  windows: 'Windows',
};

/**
 * SIL-1213: a THIRD value `detectProbeOs` can return, distinct from
 * `ProbeOs` - the probe script itself exits immediately on a non-Linux host
 * (apps/verify-harness/runner/inventory-probe.v5.sh), so there is no
 * "macOS runs the Linux probe under the hood" - that claim was wrong (the
 * comment this replaces asserted it, citing PR #1845, but PR #1845 covered
 * WSL detection, not macOS). A macOS browser must never auto-select the
 * Linux tab as if a command copied there will run - `detectProbeOs`
 * reports 'macos' distinctly so the caller can show an explicit
 * not-supported notice instead of silently handing out a command that
 * cannot work on the machine running the browser.
 */
export type DetectedOs = ProbeOs | 'macos';

/**
 * Best-effort detection of the OS running the browser, to auto-select the
 * matching tab instead of defaulting to a fixed one. Uses only standard
 * browser APIs (`navigator.userAgentData` where available, falling back to
 * `navigator.platform`/`navigator.userAgent`) - no new dependency. Returns
 * `null` when nothing points either way, and the caller falls back to the
 * existing default tab.
 */
export function detectProbeOs(): DetectedOs | null {
  if (typeof navigator === 'undefined') return null;

  const uaData = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
  const haystack = `${uaData?.platform ?? ''} ${navigator.platform ?? ''} ${navigator.userAgent ?? ''}`.toLowerCase();

  // 'win' alone is too loose - it matches 'darwin'. Real Windows values are
  // 'Win32'/'Win64' (navigator.platform), 'Windows' (userAgentData.platform),
  // and 'Windows NT ...' (navigator.userAgent).
  if (haystack.includes('windows') || haystack.includes('win32') || haystack.includes('win64')) {
    return 'windows';
  }
  // Checked before the generic Linux branch below: 'mac'/'darwin' must not
  // fall through into 'linux' - see this function's docstring.
  if (haystack.includes('mac') || haystack.includes('darwin')) {
    return 'macos';
  }
  if (haystack.includes('linux') || haystack.includes('x11') || haystack.includes('cros')) {
    return 'linux';
  }
  return null;
}

/**
 * `sxinv_` + base64url(32 random bytes) — see
 * apps/api/src/services/inventory/tokens.ts:mintInventoryToken. 32 bytes of
 * base64url (no padding) is always 43 characters (ceil(32 * 8 / 6)).
 * base64url's alphabet is exactly `[A-Za-z0-9_-]`, which is also why the
 * token is safe to single-quote verbatim in both a POSIX shell (no `'`
 * possible in the value) and PowerShell (no `'` there either).
 */
export const INVENTORY_TOKEN_PATTERN = /^sxinv_[A-Za-z0-9_-]{43}$/;

export function isValidInventoryToken(token: string): boolean {
  return INVENTORY_TOKEN_PATTERN.test(token);
}

/**
 * SIL-1189: the only mask this page ever shows for a token — same length
 * class as the real thing (`sxinv_` + 43 masked characters) so the masked
 * command doesn't visually shrink or grow relative to the real one.
 */
const MASKED_TOKEN = `sxinv_${'•'.repeat(43)}`;

/**
 * SIL-1189: the probe command panel is the only place
 * `SILICONX_INVENTORY_TOKEN` appears on screen, so the displayed string must
 * mask it while the copied/downloaded artifact stays fully runnable.
 * `buildProbeCommandDisplay` is `buildProbeCommand`'s screen-only sibling —
 * every other character of the command, including the checksum, filenames,
 * and flags, stays exactly as built; only the token substring is swapped.
 * `null` under the same conditions `buildProbeCommand` returns `null`.
 */
export function buildProbeCommandDisplay(os: ProbeOs, token: string | null): string | null {
  const command = buildProbeCommand(os, token);
  if (!command || !token) return command;
  return command.split(token).join(MASKED_TOKEN);
}

/**
 * `null` when there is no token to embed, OR when the token does not match
 * the shape mintInventoryToken produces — the caller must treat both as "do
 * not render a command" (the second case is a defensive belt-and-braces
 * check, not an expected path: it would mean the mint response itself was
 * malformed, and this page must never build and hand out a command carrying
 * whatever that unexpected string was).
 *
 * Windows: the token is set on the PARENT PowerShell's `$env:` before
 * launching the child `-File` invocation, which inherits it, and cleared
 * immediately after so it does not linger in that shell's environment for
 * the rest of the session — the env-var prefix on the Linux side achieves
 * the same "scoped to this one command" property for free, since a `VAR=val`
 * prefix only ever applies to the command it prefixes.
 */
export function buildProbeCommand(os: ProbeOs, token: string | null): string | null {
  if (!token || !isValidInventoryToken(token)) return null;

  if (os === 'linux') {
    // SIL-1299: this used to be an `a && b || { echo ...; exit 1; }` chain
    // run directly in the user's interactive shell — any `exit 1` there
    // exits THAT shell, not a subprocess, which on macOS Terminal (and an
    // SSH session, see install-one-liner.ts/SIL-1286) destroys the window
    // and takes the error message with it before anyone can read it. Fixed
    // the same way SIL-1286 fixed the verify-harness install one-liner:
    // the whole thing runs inside a subshell `( ... )`, so `exit 1` only
    // ends the subshell — the pasting terminal always survives.
    //
    // Every failure branch also calls `_sxpause`, which prints the error,
    // then — ONLY when stdin is a terminal (`[ -t 0 ]`) — waits for a
    // keypress before the subshell exits, so the message stays on screen
    // instead of scrolling away with the prompt. A non-interactive/CI
    // invocation (stdin not a tty) skips the wait entirely and exits
    // immediately; guarding on `[ -t 0 ]` is what keeps this from becoming
    // a worse bug (a script that hangs forever in CI).
    //
    // The keypress read itself has to work under `sh`, `bash` AND `zsh`
    // (macOS's default login shell) with no assumption about which one is
    // running this pasted command. `read -n 1` is bash-only; the zsh
    // equivalent is `read -k 1` — neither is portable. `stty -echo -icanon
    // min 1 time 0` + a single-byte `dd` read IS portable across all three
    // (verified interactively via a pty harness under sh, bash, and zsh,
    // and non-interactively with stdin redirected from /dev/null under all
    // three — the guard skips the read and the subshell exits immediately,
    // no hang) and needs no Enter keypress, matching "press any key".
    // `stty -g`/`stty "$saved"` save and restore the terminal's own mode
    // rather than assuming a starting state.
    //
    // SIL-1299 also fixes the checksum step itself: macOS's own
    // `/sbin/sha256sum` accepts a `-c` checklist as a FILE ARGUMENT but not
    // piped on stdin (`echo "<sha>  <file>" | sha256sum -c` fails there
    // with a bare usage error even for a correct digest — verified). Rather
    // than depend on `-c`'s checklist-parsing quirks per platform at all,
    // this hashes the downloaded file directly (`sha256sum <file>` /
    // `shasum -a 256 <file>`, both of which print "<hex>  <file>" the same
    // way on every platform that ships them) and compares the hex digest in
    // the shell — a form that behaves identically under GNU coreutils and
    // macOS's own sha256sum, with no special-casing.
    //
    // Preference order: `sha256sum` if present (Linux; also present but
    // broken only for the piped-`-c` form on macOS, which this avoids),
    // else `shasum -a 256` (macOS's own tool; some Linux installs also
    // carry it). If NEITHER exists, that is reported as its own message —
    // "no checksum tool found", never "checksum mismatch": a missing tool
    // says nothing about whether the download is genuine, and accusing it
    // of tampering when the real cause is a missing binary was the
    // pre-existing bug on every macOS attempt.
    const pause =
      `if [ -t 0 ]; then printf '%s' 'Press any key to exit... '; ` +
      `_sxstty=$(stty -g 2>/dev/null); stty -echo -icanon min 1 time 0 2>/dev/null; ` +
      `dd bs=1 count=1 >/dev/null 2>&1; [ -n "$_sxstty" ] && stty "$_sxstty" 2>/dev/null; echo; fi`;
    const steps = [
      `_sxpause() { ${pause}; }`,
      `_sxfail() { echo "siliconx: $1" >&2; _sxpause; exit 1; }`,
      // SIL-1201 item 6: `curl -fsSL` already fails closed (nonzero exit, no
      // usable output file) on an HTTP error status, so a genuine 4xx/5xx
      // from the CDN never reaches the checksum step below on its own —
      // but curl's own terse stderr ("curl: (22) The requested URL
      // returned error: 403") is the only thing a customer would otherwise
      // see, and a network-level failure that leaves a partial/empty file
      // (a dropped connection mid-transfer) falls straight into the
      // checksum-mismatch branch, which reads as a tampering accusation
      // when the real problem is availability. Fail with an explicit,
      // distinct message before hashing anything.
      // SIL-1900: report the real cause. `-f` hid it (bare exit 22, no
      // status); without it curl exits 0 on any HTTP status, so the status
      // is read from `-w` (the last line of captured output; stderr is merged
      // in, so a transport error's text rides along) and a 4xx/5xx status (or curl's own nonzero exit)
      // fails with curl's exit code, the HTTP status, and a connectivity hint.
      `_sxdl() { _sxout=$(curl -sSL -w '\\n%{http_code}' ${PROBE_URL} -o ${INVENTORY_PROBE_FILENAME} 2>&1); _sxrc=$?; ` +
        `_sxhttp=$(printf '%s' "$_sxout" | tail -n 1); _sxmsg=$(printf '%s' "$_sxout" | sed '$d' | head -n 3 | tr '\\n' ' '); ` +
        `case "$_sxhttp" in 4??|5??) _sxbad=1;; *) _sxbad=0;; esac; if [ "$_sxrc" -ne 0 ] || [ "$_sxbad" = 1 ]; then rm -f ${INVENTORY_PROBE_FILENAME}; ` +
        `_sxfail "could not download the probe script (curl exit $_sxrc, HTTP $_sxhttp) $_sxmsg - check outbound access to verify.siliconx.tech and api.siliconx.tech (proxy, firewall, DNS, CA trust)"; fi; }`,
      `_sxdl`,
      `if [ ! -s ${INVENTORY_PROBE_FILENAME} ]; then _sxfail 'could not download the probe script - the downloaded file is empty - check outbound access to verify.siliconx.tech and api.siliconx.tech (proxy, firewall)'; fi`,
      `if command -v sha256sum >/dev/null 2>&1; then _sxsum=$(sha256sum ${INVENTORY_PROBE_FILENAME} | awk '{print $1}'); ` +
        `elif command -v shasum >/dev/null 2>&1; then _sxsum=$(shasum -a 256 ${INVENTORY_PROBE_FILENAME} | awk '{print $1}'); ` +
        `else _sxfail 'no checksum tool found (need sha256sum or shasum) - cannot verify the probe script'; fi`,
      `if [ "$_sxsum" != "${INVENTORY_PROBE_SHA256}" ]; then rm -f ${INVENTORY_PROBE_FILENAME}; _sxfail 'probe checksum mismatch - refusing to run'; fi`,
      `SILICONX_INVENTORY_TOKEN='${token}' sh ${INVENTORY_PROBE_FILENAME} --post`,
    ];
    return `( ${steps.join(' && ')} )`;
  }

  // Windows PowerShell 5.1's Invoke-WebRequest already throws a terminating
  // error on a non-2xx status (there is no -SkipHttpErrorCheck to opt out of
  // that in 5.1 — that flag is PS 7+ only), so a 403/404 from the CDN does
  // stop the chain before Get-FileHash runs. Left uncaught, though, that
  // throws PowerShell's own raw WebException text, not a message a customer
  // can act on — and it does not cover a connection that fails below the
  // HTTP layer (DNS, TLS, timeout) or a response that returns 200 with an
  // empty/short body. Wrap the download explicitly and check the file
  // before hashing, same reasoning as the Linux branch above: a download
  // failure must never read as "checksum mismatch - refusing to run".
  //
  // SIL-1299: same readability treatment as the Linux branch above, with one
  // important difference in HOW it terminates. `exit` in PowerShell closes
  // the whole session it runs in — unlike bash, there is no subshell to
  // scope it to, so using `exit` here would recreate exactly this ticket's
  // bug for a customer who pastes this into an already-open PowerShell
  // prompt (the documented, primary way to run it — see RUN_INSTRUCTIONS
  // below): pressing the key would be immediately followed by PowerShell
  // itself closing the window. `throw` does not have that problem — an
  // uncaught `throw` unwinds to the prompt and leaves the session open,
  // which is why the pre-SIL-1299 code already used it. So each failure
  // still ends in `throw`, now preceded by an explicit message + pause
  // instead of relying on PowerShell's own terminal WebException/exception
  // text (which read as a raw stack trace, not something a customer could
  // act on). The pause itself only waits when stdin is not redirected
  // (`-not [Console]::IsInputRedirected`, PowerShell's TTY-equivalent
  // check), so a non-interactive run never blocks. `$Host.UI.RawUI.ReadKey`
  // is the single-keypress read; not every PowerShell host implements it
  // (e.g. the ISE), so it falls back to `Read-Host` (needs Enter) rather
  // than throwing itself. No double quotes anywhere in this string —
  // `buildProbeLauncher` below wraps the whole thing in `-Command "..."`,
  // and an embedded `"` would break that outer quoting — so string
  // interpolation uses `+` concatenation instead of `"...$var..."`.
  const psPause =
    `if (-not [Console]::IsInputRedirected) { Write-Host 'Press any key to exit...'; ` +
    `try { $null = $Host.UI.RawUI.ReadKey('NoEcho,IncludeKeyDown') } catch { Read-Host | Out-Null } }`;
  const psFail = (msg: string) =>
    `Write-Host ('siliconx: ' + '${msg}') -ForegroundColor Red; ${psPause}; throw ('siliconx: ' + '${msg}')`;
  // SIL-1790: like psFail, plus a runtime PowerShell expression (the caught
  // exception's message) appended via `+` - it cannot live inside the
  // single-quoted literal. Surfaces the real cause (TLS / DNS / proxy).
  const psFailWithDetail = (msg: string, expr: string) =>
    `Write-Host ('siliconx: ' + '${msg}' + ${expr}) -ForegroundColor Red; ${psPause}; throw ('siliconx: ' + '${msg}' + ${expr})`;
  // SIL-1790: Windows PowerShell 4/5.1 on Server 2012 R2 defaults to
  // SSL3/TLS1.0; the CDN needs TLS 1.2. OR-in (never replace) so TLS 1.3
  // stays enabled on newer hosts. No double quotes (-Command "..." wrapper).
  const PS_FORCE_TLS12 =
    `[Net.ServicePointManager]::SecurityProtocol = [Net.ServicePointManager]::SecurityProtocol -bor [Net.SecurityProtocolType]::Tls12;`;
  // SIL-1900: preflight so an unsupported host gets a plain-language message
  // instead of a generic network error or a missing-cmdlet error. Supported
  // minimum is Server 2012 R2 (PowerShell 4.0, .NET 4.5+). A host without the
  // Tls12 enum value (old .NET) makes the TLS assignment throw; that is
  // reported as its own cause. Placed AFTER the TLS assignment so the
  // assignment itself stays the first statement.
  const PS_PREFLIGHT =
    `if ($PSVersionTable.PSVersion.Major -lt 4) { ${psFailWithDetail(
      'this machine has PowerShell too old for the probe - Windows Server 2012 R2 or later (PowerShell 4.0+) is required. Found PowerShell ',
      '$PSVersionTable.PSVersion.ToString()',
    )} }; ` +
    `if (-not ([Net.ServicePointManager]::SecurityProtocol -band [Net.SecurityProtocolType]::Tls12)) { ${psFail(
      'TLS 1.2 could not be enabled - Windows Server 2012 / 2012 R2 needs .NET Framework 4.5 or later and TLS 1.2 enabled in the OS',
    )} };`;
  // SIL-1900: Get-FileHash does not exist on PowerShell 3.0; fall back to
  // .NET SHA256 (uppercase hex, same as Get-FileHash). Resolve-Path because
  // .NET resolves relative paths against the process cwd, not PS's location.
  const psFile = `.\\${INVENTORY_PROBE_PS1_FILENAME}`;
  const PS_HASH =
    `$sxh = if (Get-Command Get-FileHash -ErrorAction SilentlyContinue) { (Get-FileHash -Algorithm SHA256 ${psFile}).Hash } ` +
    `else { [BitConverter]::ToString([Security.Cryptography.SHA256]::Create().ComputeHash([IO.File]::ReadAllBytes((Resolve-Path ${psFile}).ProviderPath))) -replace '-','' };`;
  return (
    `${PS_FORCE_TLS12} ${PS_PREFLIGHT} ` +
    `try { Invoke-WebRequest -UseBasicParsing ${PROBE_PS1_URL} -OutFile ${INVENTORY_PROBE_PS1_FILENAME} } ` +
    `catch { ${psFailWithDetail('could not download the probe script - the server returned an error or the connection failed (check outbound access to verify.siliconx.tech and api.siliconx.tech, proxy, firewall): ', '$_.Exception.Message')} }; ` +
    `if (-not (Test-Path .\\${INVENTORY_PROBE_PS1_FILENAME}) -or (Get-Item .\\${INVENTORY_PROBE_PS1_FILENAME}).Length -eq 0) ` +
    `{ ${psFail('could not download the probe script - the downloaded file is missing or empty')} }; ` +
    `${PS_HASH} ` +
    `if ($sxh -ne '${INVENTORY_PROBE_PS1_SHA256}') ` +
    `{ Remove-Item .\\${INVENTORY_PROBE_PS1_FILENAME}; ${psFail('probe checksum mismatch - refusing to run')} }; ` +
    `$env:SILICONX_INVENTORY_TOKEN='${token}'; ` +
    `powershell -NoProfile -ExecutionPolicy Bypass -File .\\${INVENTORY_PROBE_PS1_FILENAME} -Post; ` +
    `Remove-Item Env:SILICONX_INVENTORY_TOKEN`
  );
}

/**
 * SIL-1127: a one-click Windows launcher, sibling to `buildProbeCommand`.
 * Wraps the exact same Windows command string (token baked in, same
 * checksum gate) in `.cmd` syntax so a customer can double-click it instead
 * of pasting into PowerShell. No new API endpoint and no compiled binary -
 * this is built and downloaded entirely client-side via a Blob.
 *
 * `.cmd` was chosen over `.ps1` specifically because Windows' file
 * association runs a `.cmd` on double-click; a `.ps1` opens in a text
 * editor instead. `.cmd` files conventionally use CRLF line endings.
 *
 * `null` under the same conditions `buildProbeCommand('windows', token)`
 * returns `null` - no token, or a token that fails the shape check.
 */
export function buildProbeLauncher(token: string | null): string | null {
  const command = buildProbeCommand('windows', token);
  if (!command) return null;

  return [`@echo off`, `powershell -NoProfile -ExecutionPolicy Bypass -Command "${command}"`, `pause`, ''].join(
    '\r\n',
  );
}

/**
 * SIL-1197: the Windows launcher used to download as the same fixed
 * `siliconx-true-read.cmd` for every account, which made it indistinguishable
 * from any other account's launcher once saved to disk (Downloads folders,
 * shared runbooks, ticket attachments). Derives a filesystem-safe slug from
 * the account's display name (business legalName, or individual
 * firstName+lastName — see authContext's `displayName` docstring, which
 * covers both org types; `orgName` alone is null for individual accounts) so
 * the filename itself identifies which account it belongs to. Falls back to
 * `'account'` when there is nothing usable to slugify (still loading, or an
 * unexpected empty name) — the filename must never come out malformed or
 * empty.
 */
export function slugifyAccountName(name: string | null | undefined): string {
  const slug = (name ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'account';
}
